import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Loader2, Plus, Trash2, Save, Languages, CheckCircle2 } from 'lucide-react'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { useProduction } from '@/services/use-production'

const localizedStringSchema = z.object({
  tr: z.string().min(1, 'Türkçe alan zorunludur'),
  en: z.string().optional(),
  fr: z.string().optional(),
  es: z.string().optional(),
  ru: z.string().optional(),
  nl: z.string().optional(),
  ar: z.string().optional(),
})

const productionSchema = z.object({
  hero: z.object({
    title: localizedStringSchema,
    description: localizedStringSchema,
  }),
  steps: z.array(z.object({
    title: localizedStringSchema,
    duration: localizedStringSchema,
    description: localizedStringSchema,
    efficiency: z.coerce.number().min(0).max(100),
    icon: z.string().optional(),
  })),
  stats: z.array(z.object({
    value: z.string().min(1, 'Değer zorunludur'),
    label: localizedStringSchema,
  })),
})

type ProductionFormValues = z.infer<typeof productionSchema>

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
  { title: 'Üretim Süreci CMS', href: '/uretim-sureci', isActive: true },
]

const LANGUAGES = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'ar', label: 'العربية' },
]

function UretimSureciPage() {
  const { production, isLoading, updateProduction, isUpdating } = useProduction()
  const [isNormalSaving, setIsNormalSaving] = useState(false)
  const [isAISaving, setIsAISaving] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('')

  const form = useForm<ProductionFormValues>({
    resolver: zodResolver(productionSchema),
    defaultValues: {
      hero: { title: { tr: '' }, description: { tr: '' } },
      steps: [],
      stats: [],
    },
  })

  const { fields: stepFields, append: appendStep, remove: removeStep } = useFieldArray({
    control: form.control,
    name: 'steps',
  })

  const { fields: statFields, append: appendStat, remove: removeStat } = useFieldArray({
    control: form.control,
    name: 'stats',
  })

  useEffect(() => {
    if (production) {
      form.reset({
        hero: production.hero,
        steps: production.steps,
        stats: production.stats,
      })
    }
  }, [production, form])

  const onSubmit = async (values: ProductionFormValues) => {
    setIsNormalSaving(true)
    try {
      await updateProduction({ data: values })
    } finally {
      setIsNormalSaving(false)
    }
  }

  const onTranslateAndSave = async () => {
    const values = form.getValues()
    setIsAISaving(true)
    setShowProgress(true)
    setProgress(5)
    setCurrentStep('AI Çeviri servisine bağlanılıyor...')

    const steps = [
      { p: 20, m: 'Giriş (Hero) bölümü çeviriliyor...' },
      { p: 50, m: 'Üretim adımları çeviriliyor...' },
      { p: 80, m: 'İstatistikler ve veriler çeviriliyor...' },
      { p: 98, m: 'Veriler işleniyor, bu biraz zaman alabilir...' },
    ]

    let currentStepIdx = 0
    const interval = setInterval(() => {
      if (currentStepIdx < steps.length) {
        setProgress(steps[currentStepIdx].p)
        setCurrentStep(steps[currentStepIdx].m)
        currentStepIdx++
      }
    }, 1200)

    try {
      await updateProduction({ data: values, translate: true })
      clearInterval(interval)
      setProgress(100)
      setCurrentStep('Tüm diller başarıyla kaydedildi!')
      setTimeout(() => setShowProgress(false), 2000)
    } catch (error) {
      clearInterval(interval)
      setShowProgress(false)
    } finally {
      setIsAISaving(false)
    }
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[80vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Üretim Süreci Yönetimi</h1>
                <p className="text-muted-foreground text-sm">Üretim adımları, verimlilik oranları ve istatistikleri düzenleyin.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onTranslateAndSave}
                  disabled={isUpdating}
                  className="w-full sm:w-auto border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                >
                  {isAISaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  AI ile Çevir ve Kaydet
                </Button>
                <Button type="submit" disabled={isUpdating} className="w-full sm:w-auto">
                  {isNormalSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Normal Kaydet
                </Button>
              </div>
            </div>

            <Tabs defaultValue="hero" className="w-full">
              <TabsList className="flex flex-wrap h-auto w-full">
                <TabsTrigger value="hero" className="flex-1">Giriş (Hero)</TabsTrigger>
                <TabsTrigger value="steps" className="flex-1">Üretim Adımları</TabsTrigger>
                <TabsTrigger value="stats" className="flex-1">İstatistikler</TabsTrigger>
              </TabsList>

              {/* HERO SECTION */}
              <TabsContent value="hero" className="space-y-4 pt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Giriş Bölümü</CardTitle>
                    <CardDescription>Sayfanın en üstünde görünen başlık ve kısa açıklama.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Tabs defaultValue="tr">
                      <TabsList>
                        {LANGUAGES.map(lang => (
                          <TabsTrigger key={lang.code} value={lang.code}>{lang.label}</TabsTrigger>
                        ))}
                      </TabsList>
                      {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                          <FormField
                            control={form.control}
                            name={`hero.title.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Başlık ({lang.label})</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Örn: Üretim Sürecimiz" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`hero.description.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Açıklama ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} placeholder="Giriş açıklaması..." rows={3} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* STEPS SECTION */}
              <TabsContent value="steps" className="space-y-4 pt-4">
                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={() => appendStep({ title: { tr: '' }, duration: { tr: '' }, description: { tr: '' }, efficiency: 100, icon: 'Settings' })}>
                    <Plus className="mr-2 h-4 w-4" /> Adım Ekle
                  </Button>
                </div>
                <div className="grid gap-6">
                  {stepFields.map((field, index) => (
                    <Card key={field.id}>
                      <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-md font-semibold">Adım #{index + 1}</CardTitle>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeStep(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="grid gap-4 md:grid-cols-2">
                          <FormField
                            control={form.control}
                            name={`steps.${index}.efficiency`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Verimlilik Oranı (%)</FormLabel>
                                <FormControl>
                                  <Input type="number" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`steps.${index}.icon`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>İkon (Lucide adı)</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Settings, Scissors, Paintbrush vb." />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <Tabs defaultValue="tr" className="w-full">
                          <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                            {LANGUAGES.map(lang => (
                              <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.label}</TabsTrigger>
                            ))}
                          </TabsList>
                          {LANGUAGES.map(lang => (
                            <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                              <div className="grid gap-4 md:grid-cols-2">
                                <FormField
                                  control={form.control}
                                  name={`steps.${index}.title.${lang.code}` as any}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Başlık ({lang.label})</FormLabel>
                                      <FormControl>
                                        <Input {...field} placeholder="Örn: Lazer Kesim" />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name={`steps.${index}.duration.${lang.code}` as any}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Süre ({lang.label})</FormLabel>
                                      <FormControl>
                                        <Input {...field} placeholder="Örn: 2-3 Gün" />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                              <FormField
                                control={form.control}
                                name={`steps.${index}.description.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Açıklama ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Textarea {...field} rows={2} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* STATS SECTION */}
              <TabsContent value="stats" className="space-y-4 pt-4">
                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={() => appendStat({ value: '', label: { tr: '' } })}>
                    <Plus className="mr-2 h-4 w-4" /> İstatistik Ekle
                  </Button>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {statFields.map((field, index) => (
                    <Card key={field.id} className="flex flex-col">
                      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                        <CardTitle className="text-sm font-medium">İstatistik #{index + 1}</CardTitle>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeStat(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-4 flex-1">
                        <FormField
                          control={form.control}
                          name={`stats.${index}.value`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Değer</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Örn: 500+ / Ton/Ay" className="h-8 text-sm" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Tabs defaultValue="tr" className="w-full">
                          <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                            {LANGUAGES.map(lang => (
                              <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.label}</TabsTrigger>
                            ))}
                          </TabsList>
                          {LANGUAGES.map(lang => (
                            <TabsContent key={lang.code} value={lang.code} className="pt-2">
                              <FormField
                                control={form.control}
                                name={`stats.${index}.label.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Etiket ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Örn: Üretim Kapasitesi" className="h-8 text-sm" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </form>
        </Form>
      </Main>

      <Dialog open={showProgress} onOpenChange={setShowProgress}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5 text-blue-600" />
              Yapay Zeka Çevirisi
            </DialogTitle>
            <DialogDescription>
              İçeriğiniz 7 farklı dile profesyonelce çevriliyor. Lütfen bekleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm font-medium">
                <span>{currentStep}</span>
                <span>%{progress}</span>
              </div>
              <Progress value={progress} className="h-2 w-full" />
            </div>
            {progress === 100 && (
              <div className="flex items-center justify-center gap-2 text-green-600 animate-in fade-in zoom-in duration-300">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium text-sm">Başarıyla tamamlandı ve kaydedildi!</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export const Route = createFileRoute('/_authenticated/uretim-sureci/')({
  component: UretimSureciPage,
})
